import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStatusDotClass, resolveStatus } from '@/utils/clusterHealth';
import {
  Shield,
  AlertTriangle,
  TrendingUp,
  Server,
  FileSearch,
  Filter,
  Users,
  Package,
  Network,
  Lock,
  HardDrive,
  Settings,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Clock,
  CheckCircle,
  Loader2,
  Play,
  Cloud,
  ShipWheel,
  RefreshCw,
  FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';


import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { CircularProgress } from '@/components/ui/circular-progress';
import { apiClient, VulnerabilityDashboard as VulnerabilityDashboardData, VulnerabilityFinding, FindingValidationResult, Cluster, TriggerRiskAssessmentRequest, cleanErrorMessage, AWSConnection, OCIConnection, CloudRiskAssessment, OCIRiskAssessment, CloudVulnerabilityDashboardResponse, CloudUniqueFinding, CloudAttackChain } from '@/api/client';
import RiskTrendsTab from '@/components/RiskTrendsTab';
import CloudRiskTrendsTab from '@/components/CloudRiskTrendsTab';
import AttackChainVisualization from '@/components/AttackChainVisualization';
import CloudAttackChainVisualization from '@/components/CloudAttackChainVisualization';

// Category icons mapping (K8s)
const categoryIcons: Record<string, React.ComponentType<any>> = {
  'Network Security': Network,
  'RBAC Security': Users,
  'Privilege Security': Lock,
  'Container Image Security': Package,
  'Resource Usage Security': Settings,
  'Storage Security': HardDrive,
};

// Cloud category icons mapping
const cloudCategoryIcons: Record<string, React.ComponentType<any>> = {
  'Access Control': Users,
  'Credential Security': Lock,
  'Policy Security': Shield,
  'Compliance': FileSearch,
  'Network Security': Network,
  'Data Protection': HardDrive,
};

// Cloud categories list
const cloudCategories = [
  'Access Control',
  'Credential Security',
  'Policy Security',
  'Compliance',
  'Network Security',
  'Data Protection',
];



// Helper function to parse protobuf timestamp
const parseTimestamp = (timestamp: any): string => {
  if (!timestamp) return new Date().toISOString();
  
  if (typeof timestamp === 'string') {
    return timestamp;
  }
  
  if (timestamp.seconds) {
    // Protobuf timestamp format
    const milliseconds = parseInt(timestamp.seconds) * 1000 + Math.floor((timestamp.nanos || 0) / 1000000);
    return new Date(milliseconds).toISOString();
  }
  
  return new Date().toISOString();
};

const VulnerabilityDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [rawDashboardData, setRawDashboardData] = useState<VulnerabilityDashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedSeverities, setSelectedSeverities] = useState<string[]>([]);
  const [selectedFixStatus, setSelectedFixStatus] = useState<string[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const findingsPerPage = 10;

  // Scan trigger state
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [showScanDialog, setShowScanDialog] = useState(false);
  const [selectedClusterForScan, setSelectedClusterForScan] = useState<string>('');
  const [isStartingScan, setIsStartingScan] = useState(false);
  const [scanError, setScanError] = useState<string>('');

  // Cluster filter state (controls all tabs)
  const [selectedCluster, setSelectedCluster] = useState<string>('all'); // 'all' = all clusters

  // Attack chains state
  const [attackChains, setAttackChains] = useState<any[]>([]);
  const [isLoadingAttackChains, setIsLoadingAttackChains] = useState(false);
  const [attackChainsError, setAttackChainsError] = useState('');

  // Ongoing assessments state
  const [ongoingAssessments, setOngoingAssessments] = useState<Array<{cluster_id: string; cluster_name: string; progress: number}>>([]);

  // Cloud risk assessment state
  const [awsConnections, setAwsConnections] = useState<AWSConnection[]>([]);
  const [ociConnections, setOciConnections] = useState<OCIConnection[]>([]);
  const [selectedAwsAccount, setSelectedAwsAccount] = useState<string>('');
  const [selectedOciConnection, setSelectedOciConnection] = useState<string>('');
  const [selectedCloudProvider, setSelectedCloudProvider] = useState<'aws' | 'oci'>('aws');
  const [showCloudScanDialog, setShowCloudScanDialog] = useState(false);
  const [isStartingCloudScan, setIsStartingCloudScan] = useState(false);
  const [cloudScanError, setCloudScanError] = useState<string>('');
  const [ongoingCloudAssessments, setOngoingCloudAssessments] = useState<CloudRiskAssessment[]>([]);
  const [ongoingOciAssessments, setOngoingOciAssessments] = useState<OCIRiskAssessment[]>([]);
  const [, setAllCloudAssessments] = useState<CloudRiskAssessment[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedCloudAssessment, setSelectedCloudAssessment] = useState<CloudRiskAssessment | null>(null);
  const [showCloudAssessmentDetails, setShowCloudAssessmentDetails] = useState(false);

  // Cloud Unique Findings Dashboard state
  const [cloudDashboardData, setCloudDashboardData] = useState<CloudVulnerabilityDashboardResponse | null>(null);
  const [cloudUniqueFindings, setCloudUniqueFindings] = useState<CloudUniqueFinding[]>([]);
  const [cloudAccountFilter, setCloudAccountFilter] = useState<string>('all');
  const [isLoadingCloudDashboard, setIsLoadingCloudDashboard] = useState(false);
  const [cloudDashboardError, setCloudDashboardError] = useState<string>('');
  const [selectedCloudFinding, setSelectedCloudFinding] = useState<CloudUniqueFinding | null>(null);
  const [showCloudFindingDetails, setShowCloudFindingDetails] = useState(false);
  const [isDismissingFinding, setIsDismissingFinding] = useState(false);

  // Cloud filter state (mirroring K8s filters)
  const [cloudSelectedCategories, setCloudSelectedCategories] = useState<string[]>([]);
  const [cloudSelectedSeverities, setCloudSelectedSeverities] = useState<string[]>([]);
  const [cloudSelectedStatus, setCloudSelectedStatus] = useState<string[]>([]);
  const [cloudExpandedCategories, setCloudExpandedCategories] = useState<Set<string>>(new Set());

  // Cloud Attack Chains state
  const [cloudAttackChains, setCloudAttackChains] = useState<CloudAttackChain[]>([]);
  const [isLoadingCloudAttackChains, setIsLoadingCloudAttackChains] = useState(false);
  const [isGeneratingCloudAttackChains, setIsGeneratingCloudAttackChains] = useState(false);
  const [cloudAttackChainsError, setCloudAttackChainsError] = useState<string>('');
  const cloudAttackChainsPollingRef = useRef<NodeJS.Timeout | null>(null);
  // Ref to track current cloudAccountFilter for polling guard (avoids stale closure issues)
  const cloudAccountFilterRef = useRef<string>(cloudAccountFilter);

  // Parent tab state (Kubernetes vs Cloud)
  const [activeSecurityTab, setActiveSecurityTab] = useState<string>('kubernetes');

  // Executive Summary export state
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [reportError, setReportError] = useState<string>('');

  // Load clusters for scan trigger
  useEffect(() => {
    const loadClusters = async () => {
      try {
        const response = await apiClient.listClusters();
        setClusters(response.clusters || []);
      } catch (err) {
        console.error('Failed to load clusters:', err);
      }
    };
    loadClusters();
  }, []);

  // Load AWS connections for cloud risk assessment
  // Filter to only show CloudIngestionRole connections (not VPCFlowLogsRole)
  // VPCFlowLogsRole doesn't have permissions for security assessments
  useEffect(() => {
    const loadAwsConnections = async () => {
      try {
        const response = await apiClient.getAWSConnectionStatus();
        // Filter to only include CloudIngestionRole connections for risk assessments
        const cloudIngestionConnections = (response.connections || []).filter(
          conn => conn.role_arn?.includes('CloudIngestionRole')
        );
        setAwsConnections(cloudIngestionConnections);
      } catch (err) {
        console.error('Failed to load AWS connections:', err);
      }
    };
    loadAwsConnections();
  }, []);

  // Load OCI connections for cloud risk assessment
  useEffect(() => {
    const loadOciConnections = async () => {
      try {
        const response = await apiClient.getOCIConnectionStatus();
        setOciConnections(response.connections || []);
      } catch (err) {
        console.error('Failed to load OCI connections:', err);
      }
    };
    loadOciConnections();
  }, []);

  const loadDashboardData = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      // Load dashboard data with cluster filter
      const clusterId = selectedCluster === 'all' ? undefined : selectedCluster;
      const data = await apiClient.getVulnerabilityDashboard({
        cluster_id: clusterId
      });

      // Enhance findings with validation status by fetching validation results for each assessment
      const enhancedData = await enhanceWithValidationData(data);
      setRawDashboardData(enhancedData);
    } catch (err: any) {
      console.error('Failed to load vulnerability dashboard:', err);
      setError(err.message || 'Failed to load vulnerability dashboard');
    } finally {
      setIsLoading(false);
         }
   }, [selectedCluster]);

   useEffect(() => {
     loadDashboardData();
   }, [loadDashboardData]); // Only load once on mount

   // Load attack chains based on selected cluster filter
   const loadAttackChains = useCallback(async () => {
     setIsLoadingAttackChains(true);
     setAttackChainsError('');
     try {
       const clusterId = selectedCluster === 'all' ? undefined : selectedCluster;
       const response = await apiClient.getAttackChainsByCluster(clusterId);
       setAttackChains(response.attack_chains || []);
     } catch (err: any) {
       console.error('Failed to load attack chains:', err);
       setAttackChainsError(err.message || 'Failed to load attack chains');
     } finally {
       setIsLoadingAttackChains(false);
     }
   }, [selectedCluster]);

   useEffect(() => {
     loadAttackChains();
   }, [loadAttackChains]); // Reload when cluster filter changes

   // Check ongoing K8s assessments - extracted as reusable function
   const checkOngoingAssessments = async () => {
     try {
       const response = await apiClient.listRiskAssessments(undefined, 50);
       const ongoing = response.assessments
         .filter(a => a.status === 'ongoing')
         .map(a => ({ cluster_id: a.cluster_id, cluster_name: a.cluster_name, progress: a.progress ?? 0 }));
       setOngoingAssessments(ongoing);
     } catch (err) {
       console.error('Failed to check ongoing assessments:', err);
     }
   };

   // Load ongoing assessments once on mount
   useEffect(() => {
     checkOngoingAssessments();
   }, []);

   // Load cloud assessments - extracted as reusable function
   const loadCloudAssessments = async () => {
     try {
       // Load AWS assessments
       const awsResponse = await apiClient.listCloudRiskAssessments(50);
       setAllCloudAssessments(awsResponse.assessments || []);
       const ongoingAws = (awsResponse.assessments || [])
         .filter(a => a.status === 'pending' || a.status === 'running');
       setOngoingCloudAssessments(ongoingAws);

       // Load OCI assessments
       const ociResponse = await apiClient.listOCIRiskAssessments(undefined, 50);
       const ongoingOci = (ociResponse.assessments || [])
         .filter(a => a.status === 'pending' || a.status === 'running');
       setOngoingOciAssessments(ongoingOci);
     } catch (err) {
       console.error('Failed to load cloud assessments:', err);
     }
   };

   // Poll ongoing assessments every 60 seconds for progress updates
   useEffect(() => {
     const hasOngoing = ongoingAssessments.length > 0
       || ongoingCloudAssessments.length > 0
       || ongoingOciAssessments.length > 0;
     if (!hasOngoing) return;
     const interval = setInterval(() => {
       checkOngoingAssessments();
       loadCloudAssessments();
     }, 60000);
     return () => clearInterval(interval);
   }, [ongoingAssessments.length, ongoingCloudAssessments.length, ongoingOciAssessments.length]);

   // Load cloud unique findings dashboard - reusable function (supports AWS and OCI)
   const loadCloudUniqueFindingsDashboard = useCallback(async () => {
     setIsLoadingCloudDashboard(true);
     setCloudDashboardError('');
     try {
       // Parse filter: "all", "aws:connectionId", or "oci:connectionId"
       let params: { aws_connection_id?: string; oci_connection_id?: string; cloud_provider?: string } = {};
       if (cloudAccountFilter !== 'all') {
         const [provider, connectionId] = cloudAccountFilter.split(':');
         if (provider === 'aws') {
           params.aws_connection_id = connectionId;
         } else if (provider === 'oci') {
           params.oci_connection_id = connectionId;
         }
       }
       const response = await apiClient.getCloudVulnerabilityDashboard(params);
       setCloudDashboardData(response);
       setCloudUniqueFindings(response.summary?.findings || []);
     } catch (err: any) {
       console.error('Failed to load cloud unique findings:', err);
       setCloudDashboardError(err.message || 'Failed to load cloud vulnerability dashboard');
     } finally {
       setIsLoadingCloudDashboard(false);
     }
   }, [cloudAccountFilter]);

   // Dismiss cloud finding
   const handleDismissCloudFinding = async (findingId: string, reason?: string) => {
     setIsDismissingFinding(true);
     try {
       await apiClient.dismissCloudUniqueFinding(findingId, reason);
       // Refresh the dashboard
       await loadCloudUniqueFindingsDashboard();
       setShowCloudFindingDetails(false);
       setSelectedCloudFinding(null);
     } catch (err: any) {
       console.error('Failed to dismiss finding:', err);
     } finally {
       setIsDismissingFinding(false);
     }
   };

   // Load cloud assessments once on mount
   useEffect(() => {
     loadCloudAssessments();
   }, []);

   // Load cloud unique findings when account filter changes or cloud tab is active
   useEffect(() => {
     if (activeSecurityTab === 'cloud' && awsConnections.length > 0) {
       loadCloudUniqueFindingsDashboard();
     }
   }, [activeSecurityTab, awsConnections.length, loadCloudUniqueFindingsDashboard]);

   // Keep cloudAccountFilterRef in sync with cloudAccountFilter state
   useEffect(() => {
     cloudAccountFilterRef.current = cloudAccountFilter;
   }, [cloudAccountFilter]);

   // Load cloud attack chains
   const loadCloudAttackChains = useCallback(async () => {
     setIsLoadingCloudAttackChains(true);
     setCloudAttackChainsError('');
     try {
       // Parse filter: "all", "aws:connectionId", or "oci:connectionId"
       let awsConnectionId: string | undefined;
       let ociConnectionId: string | undefined;
       if (cloudAccountFilter !== 'all') {
         const [provider, connectionId] = cloudAccountFilter.split(':');
         if (provider === 'aws') {
           awsConnectionId = connectionId;
         } else if (provider === 'oci') {
           ociConnectionId = connectionId;
         }
       }

       const response = await apiClient.getCloudAttackChains({ awsConnectionId, ociConnectionId });
       setCloudAttackChains(response.attack_chains || []);
     } catch (err: any) {
       console.error('Failed to load cloud attack chains:', err);
       setCloudAttackChainsError(err?.message || String(err) || 'Failed to load cloud attack chains');
     } finally {
       setIsLoadingCloudAttackChains(false);
     }
   }, [cloudAccountFilter]);

   // Generate cloud attack chains with polling
   const handleGenerateCloudAttackChains = async () => {
     if (!cloudAccountFilter || cloudAccountFilter === 'all') return;

     // Parse provider and connection ID from filter
     const [provider, connectionId] = cloudAccountFilter.split(':');
     if (!connectionId || (provider !== 'aws' && provider !== 'oci')) {
       setCloudAttackChainsError('Please select a specific AWS or OCI account');
       return;
     }

     // Capture the target at start to use consistently throughout polling
     const targetConnectionId = connectionId;
     const targetProvider = provider;

     // Clear any existing polling
     if (cloudAttackChainsPollingRef.current) {
       clearTimeout(cloudAttackChainsPollingRef.current);
       cloudAttackChainsPollingRef.current = null;
     }

     setIsGeneratingCloudAttackChains(true);
     setCloudAttackChainsError('');

     // Capture initial chain IDs and latest timestamp for robust change detection
     const initialChainIds = new Set(cloudAttackChains.map(c => c.id));
     const initialLatestUpdate = cloudAttackChains.reduce((latest, chain) => {
       const chainUpdate = chain.updated_at || chain.created_at;
       return chainUpdate && chainUpdate > latest ? chainUpdate : latest;
     }, '');

     try {
       // Call the appropriate generate method based on provider
       if (targetProvider === 'oci') {
         await apiClient.generateOCIAttackChains(targetConnectionId);
       } else {
         await apiClient.generateCloudAttackChains(targetConnectionId);
       }

       // Start polling for results
       const maxAttempts = 12; // 12 attempts * 5 seconds = 60 seconds max
       const pollInterval = 5000; // 5 seconds
       let attempts = 0;

       const pollForResults = async () => {
         attempts++;

         // Guard: Check if user changed the account filter mid-poll
         const currentFilter = cloudAccountFilterRef.current;
         const [currentProvider, currentId] = currentFilter.split(':');
         if (currentId !== targetConnectionId || currentProvider !== targetProvider) {
           cloudAttackChainsPollingRef.current = null;
           return;
         }

         try {
           // Fetch with the appropriate connection ID based on provider
           const fetchOptions = targetProvider === 'oci'
             ? { ociConnectionId: targetConnectionId }
             : { awsConnectionId: targetConnectionId };
           const response = await apiClient.getCloudAttackChains(fetchOptions);
           const newChains = response.attack_chains || [];

           // Guard again after async call in case account changed during fetch
           const [updatedProvider, updatedId] = cloudAccountFilterRef.current.split(':');
           if (updatedId !== targetConnectionId || updatedProvider !== targetProvider) {
             cloudAttackChainsPollingRef.current = null;
             return;
           }

           // Check for new/updated chains using IDs and timestamps (more robust than length)
           const newLatestUpdate = newChains.reduce((latest: string, chain: CloudAttackChain) => {
             const chainUpdate = chain.updated_at || chain.created_at;
             return chainUpdate && chainUpdate > latest ? chainUpdate : latest;
           }, '');

           // Detect changes: new chain IDs, different count, or newer timestamp
           const hasNewChains = newChains.some((c: CloudAttackChain) => !initialChainIds.has(c.id));
           const hasNewerUpdate = newLatestUpdate > initialLatestUpdate;
           const hasCountChange = newChains.length !== initialChainIds.size;

           if (hasNewChains || hasNewerUpdate || hasCountChange) {
             setCloudAttackChains(newChains);
             setIsGeneratingCloudAttackChains(false);
             cloudAttackChainsPollingRef.current = null;
             return;
           }

           // Continue polling if under max attempts
           if (attempts < maxAttempts) {
             cloudAttackChainsPollingRef.current = setTimeout(pollForResults, pollInterval);
           } else {
             // Max attempts reached - load whatever we have
             setCloudAttackChains(newChains);
             setIsGeneratingCloudAttackChains(false);
             cloudAttackChainsPollingRef.current = null;
           }
         } catch (err) {
           console.error('Failed to poll cloud attack chains:', err);
           // Only update error state if account filter hasn't changed
           const [errProvider, errId] = cloudAccountFilterRef.current.split(':');
           if (errId === targetConnectionId && errProvider === targetProvider) {
             setCloudAttackChainsError('Failed to load generated attack chains');
             setIsGeneratingCloudAttackChains(false);
           }
           cloudAttackChainsPollingRef.current = null;
         }
       };

       // Start first poll after initial delay
       cloudAttackChainsPollingRef.current = setTimeout(pollForResults, pollInterval);

     } catch (err: any) {
       console.error('Failed to generate cloud attack chains:', err);
       setCloudAttackChainsError(err.message || 'Failed to generate cloud attack chains');
       setIsGeneratingCloudAttackChains(false);
       cloudAttackChainsPollingRef.current = null;
     }
   };

   // Cleanup polling on unmount or when cloudAccountFilter changes
   // This ensures that changing accounts mid-poll clears the old poll and resets state
   useEffect(() => {
     return () => {
       if (cloudAttackChainsPollingRef.current) {
         clearTimeout(cloudAttackChainsPollingRef.current);
         cloudAttackChainsPollingRef.current = null;
       }
       // Reset generating state when account filter changes to prevent stale UI
       setIsGeneratingCloudAttackChains(false);
     };
   }, [cloudAccountFilter]);

   // Load cloud attack chains when cloud tab is active
   useEffect(() => {
     if (activeSecurityTab === 'cloud') {
       loadCloudAttackChains();
     }
   }, [activeSecurityTab, loadCloudAttackChains]);

   // Handle scan trigger
   const handleStartScan = async () => {
     if (!selectedClusterForScan) {
       return;
     }

     setScanError('');
     setIsStartingScan(true);

     try {
       const request: TriggerRiskAssessmentRequest = {
         cluster_id: selectedClusterForScan
       };

       await apiClient.triggerRiskAssessment(request);

       // Clear form and close modal on success
       setSelectedClusterForScan('');
       setScanError('');
       setShowScanDialog(false);

       // Immediately update ongoing assessments banner (no page refresh needed)
       setTimeout(async () => {
         await checkOngoingAssessments();
       }, 500);

       // Also reload dashboard after a longer delay for full data
       setTimeout(() => {
         loadDashboardData();
       }, 2000);
     } catch (err: any) {
       let errorMessage = cleanErrorMessage(err) || 'Failed to start risk assessment';

       if (errorMessage.includes('tier limit') || errorMessage.includes('limit reached') || errorMessage.includes('tier allows maximum')) {
         errorMessage = `${errorMessage.trim()}\n\nPlease contact hello@usekestrel.ai to upgrade your plan.`;
       }

       setScanError(errorMessage);
     } finally {
       setIsStartingScan(false);
     }
   };

   // Handle executive summary PDF generation and download
   const handleGenerateExecutiveSummary = async () => {
     setIsGeneratingReport(true);
     setReportError('');

     try {
       const blob = await apiClient.generateExecutiveSummary();

       // Create download link and trigger download
       const url = window.URL.createObjectURL(blob);
       const link = document.createElement('a');
       link.href = url;
       link.download = `executive_security_summary_${new Date().toISOString().split('T')[0]}.pdf`;
       document.body.appendChild(link);
       link.click();

       // Delay cleanup to ensure browser has started the download
       setTimeout(() => {
         document.body.removeChild(link);
         window.URL.revokeObjectURL(url);
       }, 1000);
     } catch (err: any) {
       console.error('Failed to generate executive summary:', err);
       setReportError(cleanErrorMessage(err) || 'Failed to generate executive summary');
     } finally {
       setIsGeneratingReport(false);
     }
   };

   // Handle cloud risk assessment trigger
   const handleStartCloudScan = async () => {
     // Validate based on selected provider
     if (selectedCloudProvider === 'aws' && !selectedAwsAccount) {
       return;
     }
     if (selectedCloudProvider === 'oci' && !selectedOciConnection) {
       return;
     }

     setCloudScanError('');
     setIsStartingCloudScan(true);

     try {
       if (selectedCloudProvider === 'aws') {
         // Call the AWS API to trigger cloud risk assessment
         const response = await apiClient.triggerCloudRiskAssessment({
           aws_connection_id: selectedAwsAccount
         });
         setSelectedAwsAccount('');
       } else {
         // Call the OCI API to trigger cloud risk assessment
         const response = await apiClient.triggerOCIRiskAssessment({
           oci_connection_id: selectedOciConnection
         });
         setSelectedOciConnection('');
       }

       // Close dialog and show results
       setShowCloudScanDialog(false);

       // Poll for the assessment to appear in the list after a brief delay
       setTimeout(async () => {
         await loadCloudAssessments();
       }, 1000);

     } catch (err: any) {
       const errorMessage = cleanErrorMessage(err) || 'Failed to start cloud risk assessment';
       setCloudScanError(errorMessage);
     } finally {
       setIsStartingCloudScan(false);
     }
   };

   // Reset pagination when filters change
   useEffect(() => {
     setCurrentPage(1);
   }, [selectedCategories, selectedSeverities, selectedFixStatus]);

   // Helper function to enhance dashboard data with validation results
  const enhanceWithValidationData = async (dashboardData: VulnerabilityDashboardData): Promise<VulnerabilityDashboardData> => {
    try {
      // Get unique assessment IDs from all findings (filter out undefined)
      const assessmentIds = Array.from(new Set(dashboardData.all_findings?.map(f => f.assessment_id).filter(id => id) || []));
      
      // Fetch validation results for each assessment
      const validationResultsMap = new Map<string, FindingValidationResult[]>();
      
      await Promise.all(assessmentIds.map(async (assessmentId) => {
        try {
          const assessmentDetails = await apiClient.getRiskAssessmentDetails(assessmentId);
          if (assessmentDetails.validation_results?.results) {
            validationResultsMap.set(assessmentId, assessmentDetails.validation_results.results);
          }
        } catch (err) {
          console.warn(`Failed to load validation results for assessment ${assessmentId}:`, err);
        }
      }));

      // Enhance findings with validation status
      const enhancedFindings = (dashboardData.all_findings || []).map(finding => {
        const validationResults = validationResultsMap.get(finding.assessment_id);
        const validationResult = validationResults?.find(vr => vr.finding_id === finding.id);
        
        return {
          ...finding,
          validation_status: validationResult?.validation_status as any || 'unknown',
          validation_summary: validationResult?.validation_summary,
          validated_at: validationResult?.validated_at,
        };
      });

      // Calculate validation statistics
      const totalValidatedFindings = enhancedFindings.filter(f => f.validation_status && f.validation_status !== 'unknown').length;
      const totalFixedFindings = enhancedFindings.filter(f => f.validation_status === 'fixed').length;
      const totalNotFixedFindings = enhancedFindings.filter(f => f.validation_status === 'not_fixed').length;

             // Enhance categories with validation data
       const enhancedCategories = (dashboardData.categories || []).map(category => {
         const enhancedTopFindings = (category.top_findings || []).map(finding => {
           const enhancedFinding = enhancedFindings.find(ef => ef.id === finding.id);
           return enhancedFinding || finding;
         });
         
         return {
           ...category,
           top_findings: enhancedTopFindings,
         };
       });

      // Enhance clusters with validation data
      const enhancedClusters = (dashboardData.clusters || []).map(cluster => {
        const enhancedCategories = (cluster.categories || []).map(category => ({
          ...category,
          // Categories in clusters don't have top_findings, so we don't need to enhance them here
        }));
        
        return {
          ...cluster,
          categories: enhancedCategories,
        };
      });

      return {
        ...dashboardData,
        all_findings: enhancedFindings,
        categories: enhancedCategories,
        clusters: enhancedClusters,
        critical_findings: (dashboardData.critical_findings || []).map(finding => {
          const enhancedFinding = enhancedFindings.find(ef => ef.id === finding.id);
          return enhancedFinding || finding;
        }),
        total_validated_findings: totalValidatedFindings,
        total_fixed_findings: totalFixedFindings,
        total_not_fixed_findings: totalNotFixedFindings,
      };
    } catch (err) {
      console.error('Failed to enhance dashboard data with validation results:', err);
      return dashboardData; // Return original data if enhancement fails
    }
  };

  // Client-side filtering of dashboard data
  const dashboardData = React.useMemo(() => {
    if (!rawDashboardData) return null;

    // Filter findings based on selected categories, severities, and fix status
    const filterFinding = (finding: VulnerabilityFinding) => {
      const categoryMatch = selectedCategories.length === 0 || selectedCategories.includes(finding.category);
      const severityMatch = selectedSeverities.length === 0 || selectedSeverities.includes(finding.severity);
      
      // Fix status filter
      let fixStatusMatch = true;
      if (selectedFixStatus.length > 0) {
        if (selectedFixStatus.includes('fixed') && finding.validation_status === 'fixed') {
          fixStatusMatch = true;
        } else if (selectedFixStatus.includes('not_fixed') &&
                   (finding.validation_status === 'not_fixed' ||
                    finding.validation_status === 'error' ||
                    finding.validation_status === 'unknown')) {
          fixStatusMatch = true;
        } else {
          fixStatusMatch = false;
        }
      }
      
      return categoryMatch && severityMatch && fixStatusMatch;
    };

    // Filter categories
    const filteredCategories = (rawDashboardData.categories || [])
      .filter(category => selectedCategories.length === 0 || selectedCategories.includes(category.name))
      .map(category => {
        const filteredTopFindings = (category.top_findings || []).filter(filterFinding);
        return {
          ...category,
          top_findings: filteredTopFindings
        };
      });

    // Filter clusters and their categories, and add findings to each category
    const filteredClusters = (rawDashboardData.clusters || []).map(cluster => {
      const filteredClusterCategories = (cluster.categories || [])
        .filter(category => selectedCategories.length === 0 || selectedCategories.includes(category.name))
        .map(category => {
          // Find findings for this cluster and category using the same data source as "By Category"
          // Use the category's top_findings from the main categories array
          const matchingCategory = (rawDashboardData.categories || [])
            .find(cat => cat.name === category.name);
          
          const allMatchingFindings = (matchingCategory?.top_findings || [])
            .filter(finding => 
              finding.cluster_id === cluster.cluster_id &&
              filterFinding(finding)
            );
          
          return {
            ...category,
            findings: allMatchingFindings
          };
        });
      
      return {
        ...cluster,
        categories: filteredClusterCategories
      };
    });

    // Filter findings lists
    const filteredAllFindings = (rawDashboardData.all_findings || []).filter(filterFinding);
    const filteredCriticalFindings = (rawDashboardData.critical_findings || []).filter(filterFinding);

    return {
      ...rawDashboardData,
      categories: filteredCategories.sort((a, b) => (b.average_severity_score || 0) - (a.average_severity_score || 0)),
      clusters: filteredClusters.sort((a, b) => (b.overall_severity_score || 0) - (a.overall_severity_score || 0)),
      all_findings: filteredAllFindings.sort((a, b) => new Date(parseTimestamp(b.created_at)).getTime() - new Date(parseTimestamp(a.created_at)).getTime()),
      critical_findings: filteredCriticalFindings.sort((a, b) => b.affected_resources_count - a.affected_resources_count)
    };
  }, [rawDashboardData, selectedCategories, selectedSeverities, selectedFixStatus]);

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800';
      case 'critical': return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800'; // Treat critical as high
      case 'medium': return 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/20 dark:text-orange-400 dark:border-orange-800';
      case 'low': return 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800';
      default: return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900/20 dark:text-gray-400 dark:border-gray-800';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'high': return <AlertTriangle className="h-4 w-4 text-red-600" />;
      case 'critical': return <AlertTriangle className="h-4 w-4 text-red-600" />; // Treat critical as high
      case 'medium': return <AlertTriangle className="h-4 w-4 text-orange-600" />;
      case 'low': return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      default: return <AlertTriangle className="h-4 w-4 text-gray-600" />;
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 7) return 'text-red-600 dark:text-red-400';
    if (score >= 4) return 'text-orange-600 dark:text-orange-400';
    if (score >= 2) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-green-600 dark:text-green-400';
  };

  // Normalize severity to treat critical as high
  const normalizeSeverity = (severity: string) => {
    return severity === 'critical' ? 'high' : severity;
  };

  // Get cluster name by cluster ID
  const getClusterName = (clusterId: string) => {
    const cluster = (rawDashboardData?.clusters || []).find(c => c.cluster_id === clusterId);
    return cluster?.cluster_name || clusterId;
  };

  // Get fix status display info
  const getFixStatusInfo = (finding: VulnerabilityFinding) => {
    switch (finding.validation_status) {
      case 'fixed':
        return {
          icon: <CheckCircle className="h-3 w-3 text-green-600" />,
          badge: 'FIXED',
          badgeClass: 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800',
          tooltip: 'This security finding has been successfully remediated'
        };
      case 'not_fixed':
        return {
          icon: <AlertTriangle className="h-3 w-3 text-red-600" />,
          badge: 'NOT FIXED',
          badgeClass: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800',
          tooltip: 'This security finding has not been remediated yet'
        };
      case 'error':
        return {
          icon: <AlertTriangle className="h-3 w-3 text-orange-600" />,
          badge: 'VALIDATION ERROR',
          badgeClass: 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/20 dark:text-orange-400 dark:border-orange-800',
          tooltip: 'Unable to validate if this finding has been fixed'
        };
      default:
        return null; // Don't show status if unknown or not validated
    }
  };

  const toggleCategoryExpansion = (categoryName: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryName)) {
        newSet.delete(categoryName);
      } else {
        newSet.add(categoryName);
      }
      return newSet;
    });
  };

  const toggleCloudCategoryExpansion = (categoryName: string) => {
    setCloudExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryName)) {
        newSet.delete(categoryName);
      } else {
        newSet.add(categoryName);
      }
      return newSet;
    });
  };

  const navigateToFinding = (finding: VulnerabilityFinding) => {
    // Use simpler route for unique findings that don't have assessment_id
    navigate(`/findings/${finding.id}`);
  };

  const navigateToCloudFinding = (finding: CloudUniqueFinding) => {
    navigate(`/cloud-findings/${finding.id}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">
          <AlertTriangle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Error Loading Dashboard</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={loadDashboardData}>Try Again</Button>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">
          <FileSearch className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">No Data Available</h2>
          <p className="text-gray-600">No vulnerability data found.</p>
        </div>
      </div>
    );
  }

  const allCategories = ['Network Security', 'RBAC Security', 'Privilege Security', 'Container Image Security', 'Resource Usage Security', 'Storage Security'];
  const allSeverities = ['high', 'medium', 'low'];

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Header Row - Kubernetes/Cloud Toggle (left) + Export Button (right) */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          {/* Parent Tabs - Kubernetes vs Cloud */}
          <Tabs value={activeSecurityTab} onValueChange={setActiveSecurityTab} className="space-y-0">
            <TabsList className="h-9 p-1 bg-muted/60 w-fit">
              <TabsTrigger value="kubernetes" className="h-7 px-3 text-xs font-medium data-[state=active]:bg-background data-[state=active]:shadow-sm">
                <ShipWheel className="h-3.5 w-3.5 mr-1.5" />
                Kubernetes
              </TabsTrigger>
              <TabsTrigger value="cloud" className="h-7 px-3 text-xs font-medium data-[state=active]:bg-background data-[state=active]:shadow-sm">
                <Cloud className="h-3.5 w-3.5 mr-1.5" />
                Cloud
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="flex items-center gap-2">
            {reportError && (
              <span className="text-sm text-destructive">{reportError}</span>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  onClick={handleGenerateExecutiveSummary}
                  disabled={isGeneratingReport}
                  className="flex items-center gap-2"
                >
                  {isGeneratingReport ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Generating (~3 mins)...
                    </>
                  ) : (
                    <>
                      <FileText className="h-4 w-4" />
                      Export Executive Summary
                    </>
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Generate a one-page PDF summary of your security posture (takes about 3 minutes)</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>

        {/* Tab Content - controlled by activeSecurityTab */}
        <Tabs value={activeSecurityTab} onValueChange={setActiveSecurityTab} className="space-y-6">

          {/* Kubernetes Security Tab */}
          <TabsContent value="kubernetes" className="space-y-6">
            {/* Controls Row - Cluster Filter, Inner Tabs, and Scan Button */}
            <Tabs defaultValue="dashboard" className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex flex-wrap items-center gap-3">
                  {/* Cluster Filter Dropdown */}
                  <Select
                    value={selectedCluster}
                    onValueChange={setSelectedCluster}
                  >
                    <SelectTrigger className="w-full sm:w-[180px]">
                      <SelectValue placeholder="All Clusters" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Clusters</SelectItem>
                      {clusters.map((cluster) => {
                        // Vulnerability scanning requires control stream
                        const effectiveStatus = (cluster.stream_health?.control === false)
                          ? 'disconnected'
                          : resolveStatus(cluster.connection_status, cluster.is_connected);
                        return (
                          <SelectItem key={cluster.id} value={cluster.id}>
                            <div className="flex items-center gap-2">
                              <div className={cn(
                                "w-2 h-2 rounded-full",
                                getStatusDotClass(effectiveStatus)
                              )} />
                              {cluster.cluster_name}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>

                  {/* Inner Tabs - Dashboard, Risk Trends, Attack Paths */}
                  <TabsList className="grid w-fit grid-cols-3">
                    <TabsTrigger value="dashboard" className="flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      Dashboard
                    </TabsTrigger>
                    <TabsTrigger value="trends" className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" />
                      Risk Trends
                    </TabsTrigger>
                    <TabsTrigger value="attack-paths" className="flex items-center gap-2">
                      <Network className="h-4 w-4" />
                      Attack Paths
                    </TabsTrigger>
                  </TabsList>

                  {/* Ongoing Assessments Indicator */}
                  {ongoingAssessments.length > 0 && (
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md cursor-default">
                            <CircularProgress
                              value={ongoingAssessments.length > 0 ? Math.round(ongoingAssessments.reduce((sum, a) => sum + a.progress, 0) / ongoingAssessments.length) : 0}
                              size={16}
                              strokeWidth={2}
                              className="text-blue-600 dark:text-blue-400"
                            />
                            <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                              {ongoingAssessments.length} assessment{ongoingAssessments.length > 1 ? 's' : ''} running
                            </span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" align="start" className="w-80">
                          <div className="space-y-2">
                            <h4 className="font-medium text-sm">Assessments in Progress</h4>
                            <div className="max-h-60 overflow-y-auto space-y-1">
                              {ongoingAssessments.map((assessment, index) => (
                                <div
                                  key={`${assessment.cluster_id}-${index}`}
                                  className="flex items-center gap-2 px-2 py-1.5 bg-muted/50 rounded-md text-sm"
                                >
                                  <CircularProgress
                                    value={assessment.progress}
                                    size={12}
                                    strokeWidth={2}
                                    className="text-blue-600 dark:text-blue-400 flex-shrink-0"
                                  />
                                  <span className="text-xs truncate">{assessment.cluster_name}</span>
                                  <span className="text-xs text-muted-foreground ml-auto">{assessment.progress}%</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>

                {/* Scan Trigger Button */}
                <Dialog open={showScanDialog} onOpenChange={setShowScanDialog}>
                  <DialogTrigger asChild>
                    <Button className="flex items-center gap-2">
                      <Play className="h-4 w-4" />
                      Run Scan
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Run Security Scan</DialogTitle>
                      <DialogDescription>
                        Select a cluster to run a comprehensive security assessment
                      </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Cluster</label>
                        <Select
                          value={selectedClusterForScan}
                          onValueChange={setSelectedClusterForScan}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select a cluster" />
                          </SelectTrigger>
                          <SelectContent sideOffset={5}>
                            {clusters.map((cluster) => {
                              // Vulnerability scanning requires control stream
                              const effectiveStatus = (cluster.stream_health?.control === false)
                                ? 'disconnected'
                                : resolveStatus(cluster.connection_status, cluster.is_connected);
                              return (
                                <SelectItem key={cluster.id} value={cluster.id}>
                                  <div className="flex items-center gap-2">
                                    <div className={cn(
                                      "w-2 h-2 rounded-full",
                                      getStatusDotClass(effectiveStatus)
                                    )} />
                                    {cluster.cluster_name}
                                    {effectiveStatus !== 'connected' && (
                                      <span className="text-xs text-muted-foreground">(disconnected)</span>
                                    )}
                                  </div>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>

                      {scanError && (
                        <Alert variant="destructive">
                          <AlertTriangle className="h-4 w-4" />
                          <AlertDescription className="whitespace-pre-line">
                            {scanError}
                          </AlertDescription>
                        </Alert>
                      )}
                    </div>

                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowScanDialog(false);
                          setSelectedClusterForScan('');
                          setScanError('');
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleStartScan}
                        disabled={!selectedClusterForScan || isStartingScan}
                      >
                        {isStartingScan ? 'Starting...' : 'Start Scan'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Dashboard View */}
              <TabsContent value="dashboard" className="space-y-6">
            {/* Overall Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Findings</p>
                <p className={cn("text-2xl font-bold", (dashboardData.total_findings || 0) === 0 ? "text-muted-foreground" : "")}>
                  {dashboardData.total_findings || 0}
                </p>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <AlertTriangle className={cn("h-8 w-8", (dashboardData.total_findings || 0) === 0 ? "text-muted-foreground" : "text-orange-500")} />
                </TooltipTrigger>
                <TooltipContent>
                  <p>{(dashboardData.total_findings || 0) === 0 ? "No security findings yet. Run risk assessments to discover vulnerabilities." : "Total security findings across all clusters"}</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Clusters Assessed</p>
                <p className={cn("text-2xl font-bold", (dashboardData.total_clusters || 0) === 0 ? "text-muted-foreground" : "")}>
                  {dashboardData.total_clusters || 0}
                </p>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Server className={cn("h-8 w-8", (dashboardData.total_clusters || 0) === 0 ? "text-muted-foreground" : "text-blue-500")} />
                </TooltipTrigger>
                <TooltipContent>
                  <p>{(dashboardData.total_clusters || 0) === 0 ? "No clusters assessed yet. Connect clusters and run risk assessments." : "Number of clusters that have been assessed for security vulnerabilities"}</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Overall Risk Score</p>
                <p className={cn("text-2xl font-bold", getScoreColor(dashboardData.overall_severity_score || 0), (dashboardData.overall_severity_score || 0) === 0 ? "text-muted-foreground" : "")}>
                  {(dashboardData.overall_severity_score || 0).toFixed(1)}/10
                </p>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <TrendingUp className={cn("h-8 w-8", (dashboardData.overall_severity_score || 0) === 0 ? "text-muted-foreground" : getScoreColor(dashboardData.overall_severity_score || 0))} />
                </TooltipTrigger>
                <TooltipContent>
                  <p>{(dashboardData.overall_severity_score || 0) === 0 ? "No risk score calculated yet. Complete risk assessments to see your security posture." : "Weighted average risk score: High=3, Medium=2, Low=1, across all findings"}</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Fixed Findings</p>
                <p className={cn("text-2xl font-bold", (dashboardData.total_fixed_findings || 0) === 0 ? "text-muted-foreground" : "text-green-600")}>
                  {dashboardData.total_fixed_findings || 0}
                </p>
                {(dashboardData.total_validated_findings || 0) > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    of {dashboardData.total_validated_findings} validated
                  </p>
                )}
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <CheckCircle className={cn("h-8 w-8", (dashboardData.total_fixed_findings || 0) === 0 ? "text-muted-foreground" : "text-green-600")} />
                </TooltipTrigger>
                <TooltipContent>
                  <p>{(dashboardData.total_fixed_findings || 0) === 0 ? "No findings have been validated as fixed yet. Run fix validation to check remediation status." : "Number of security findings that have been successfully remediated"}</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Category Filter */}
            <div>
              <h4 className="font-medium text-foreground mb-3">Filter by Category</h4>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={selectedCategories.length === 0 ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategories([])}
                >
                  All Categories
                </Button>
                {allCategories.map(category => (
                  <Button
                    key={category}
                    variant={selectedCategories.includes(category) ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      if (selectedCategories.includes(category)) {
                        setSelectedCategories(prev => prev.filter(c => c !== category));
                      } else {
                        setSelectedCategories(prev => [...prev, category]);
                      }
                    }}
                    className="text-xs"
                  >
                    {category}
                  </Button>
                ))}
              </div>
            </div>

            {/* Severity Filter */}
            <div>
              <h4 className="font-medium text-foreground mb-3">Filter by Severity</h4>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={selectedSeverities.length === 0 ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedSeverities([])}
                >
                  All Severities
                </Button>
                {allSeverities.map(severity => (
                  <Button
                    key={severity}
                    variant={selectedSeverities.includes(severity) ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      if (selectedSeverities.includes(severity)) {
                        setSelectedSeverities(prev => prev.filter(s => s !== severity));
                      } else {
                        setSelectedSeverities(prev => [...prev, severity]);
                      }
                    }}
                    className="text-xs capitalize"
                  >
                    {severity}
                  </Button>
                ))}
              </div>
            </div>

            {/* Fix Status Filter */}
            <div>
              <h4 className="font-medium text-foreground mb-3">Filter by Fix Status</h4>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={selectedFixStatus.length === 0 ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedFixStatus([])}
                >
                  All Status
                </Button>
                <Button
                  variant={selectedFixStatus.includes('fixed') ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    if (selectedFixStatus.includes('fixed')) {
                      setSelectedFixStatus(prev => prev.filter(s => s !== 'fixed'));
                    } else {
                      setSelectedFixStatus(prev => [...prev.filter(s => s !== 'not_fixed'), 'fixed']);
                    }
                  }}
                  className="text-xs"
                >
                  <CheckCircle className={cn("h-3 w-3 mr-1", selectedFixStatus.includes('fixed') ? "text-white" : "text-green-600")} />
                  Fixed
                </Button>
                <Button
                  variant={selectedFixStatus.includes('not_fixed') ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    if (selectedFixStatus.includes('not_fixed')) {
                      setSelectedFixStatus(prev => prev.filter(s => s !== 'not_fixed'));
                    } else {
                      setSelectedFixStatus(prev => [...prev.filter(s => s !== 'fixed'), 'not_fixed']);
                    }
                  }}
                  className="text-xs"
                >
                  <AlertTriangle className={cn("h-3 w-3 mr-1", selectedFixStatus.includes('not_fixed') ? "text-white" : "text-orange-600")} />
                  Not Fixed
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="categories" className="space-y-4">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="categories">By Category</TabsTrigger>
          <TabsTrigger value="recent">All Findings</TabsTrigger>
        </TabsList>

        {/* Categories Tab */}
        <TabsContent value="categories" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {(() => {
              // If we have data and categories, show them
              if (dashboardData.categories && dashboardData.categories.length > 0) {
                return dashboardData.categories.map(category => {
                  const CategoryIcon = categoryIcons[category.name] || Shield;
                  const isExpanded = expandedCategories.has(category.name);
                  
                  return (
                    <Card key={category.name} className="border-l-4 border-l-primary">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <CategoryIcon className="h-6 w-6 text-primary" />
                            <div>
                              <CardTitle className="text-lg">{category.name}</CardTitle>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="secondary" className="text-xs">
                                  {category.total_findings} findings
                                </Badge>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className={cn("text-sm font-medium cursor-help", getScoreColor(category.average_severity_score || 0))}>
                                      {(category.average_severity_score || 0).toFixed(1)}/10
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side="right">
                                    <p>Average severity score: High=3, Medium=2, Low=1, weighted by finding count</p>
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleCategoryExpansion(category.name)}
                          >
                            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Severity Breakdown */}
                        <div className="flex items-center gap-4 text-sm">
                          <div className="flex items-center gap-1">
                            <div className="w-3 h-3 bg-red-500 rounded"></div>
                            <span>{category.high_severity_count} High</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="w-3 h-3 bg-orange-500 rounded"></div>
                            <span>{category.medium_severity_count} Medium</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="w-3 h-3 bg-yellow-500 rounded"></div>
                            <span>{category.low_severity_count} Low</span>
                          </div>
                        </div>

                        {/* Top Findings */}
                        {isExpanded && (() => {
                          // Use top_findings if available, otherwise filter from all_findings
                          const categoryFindings = (category.top_findings && category.top_findings.length > 0)
                            ? category.top_findings
                            : (dashboardData.all_findings || []).filter(f => f.category === category.name);

                          return categoryFindings.length > 0 ? (
                            <div className="space-y-2">
                              <Separator />
                              <h5 className="font-medium text-foreground">Top Findings</h5>
                              <div className="space-y-2 max-h-80 overflow-y-auto">
                                {categoryFindings.slice(0, 5).map(finding => (
                                <div
                                  key={finding.id}
                                  className="p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                                  onClick={() => navigateToFinding(finding)}
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 mb-1">
                                        {getSeverityIcon(finding.severity)}
                                        <Badge className={cn("text-xs", getSeverityColor(normalizeSeverity(finding.severity)))}>
                                          {normalizeSeverity(finding.severity).toUpperCase()}
                                        </Badge>
                                        {(() => {
                                          const fixStatus = getFixStatusInfo(finding);
                                          return fixStatus ? (
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <Badge className={cn("text-xs", fixStatus.badgeClass)}>
                                                  {fixStatus.badge}
                                                </Badge>
                                              </TooltipTrigger>
                                              <TooltipContent>
                                                <p>{fixStatus.tooltip}</p>
                                              </TooltipContent>
                                            </Tooltip>
                                          ) : null;
                                        })()}
                                        <span className="text-xs text-muted-foreground">
                                          {finding.affected_resources_count} resources
                                        </span>
                                      </div>
                                      <h6 className="font-medium text-sm text-foreground truncate">{finding.title}</h6>
                                      <p className="text-xs text-muted-foreground line-clamp-2">{finding.description}</p>
                                    </div>
                                    <ExternalLink className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                  </div>
                                </div>
                              ))}
                              {categoryFindings.length > 5 && (
                                <div className="text-center py-2 text-xs text-muted-foreground border-t">
                                  Showing top 5 of {categoryFindings.length} findings
                                </div>
                              )}
                            </div>
                          </div>
                          ) : (
                            <div className="space-y-2">
                              <Separator />
                              <div className="text-center py-6 text-muted-foreground">
                                <Shield className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                <p className="text-sm">No findings in this category yet</p>
                                <p className="text-xs mt-1">Run a risk assessment to discover potential security issues</p>
                              </div>
                            </div>
                          );
                        })()}
                      </CardContent>
                    </Card>
                  );
                });
              }
              
              // If no data yet, show empty category cards for all known categories
              return allCategories.map(categoryName => {
                const CategoryIcon = categoryIcons[categoryName] || Shield;
                const isExpanded = expandedCategories.has(categoryName);
                
                return (
                  <Card key={categoryName} className="border-l-4 border-l-muted">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <CategoryIcon className="h-6 w-6 text-muted-foreground" />
                          <div>
                            <CardTitle className="text-lg text-muted-foreground">{categoryName}</CardTitle>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="secondary" className="text-xs">
                                0 findings
                              </Badge>
                              <span className="text-sm font-medium text-muted-foreground">
                                0.0/10
                              </span>
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleCategoryExpansion(categoryName)}
                        >
                          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Empty Severity Breakdown */}
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 bg-red-500/30 rounded"></div>
                          <span>0 High</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 bg-orange-500/30 rounded"></div>
                          <span>0 Medium</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 bg-yellow-500/30 rounded"></div>
                          <span>0 Low</span>
                        </div>
                      </div>

                      {/* Empty state when expanded */}
                      {isExpanded && (
                        <div className="space-y-2">
                          <Separator />
                          <div className="text-center py-6 text-muted-foreground">
                            <CategoryIcon className="h-8 w-8 mx-auto mb-2 opacity-30" />
                            <p className="text-sm">No {categoryName.toLowerCase()} findings yet</p>
                            <p className="text-xs mt-1">Run a risk assessment to discover potential security issues</p>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              });
            })()}
          </div>
        </TabsContent>

        {/* All Findings Tab */}
        <TabsContent value="recent" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileSearch className="h-5 w-5" />
                All Findings
                <Badge variant="outline" className="ml-2">
                  {(dashboardData.all_findings || []).length} total findings
                </Badge>
              </CardTitle>
              {(dashboardData.all_findings || []).length > 0 && (
                <div className="text-sm text-muted-foreground">
                  Showing {((currentPage - 1) * findingsPerPage) + 1}-{Math.min(currentPage * findingsPerPage, (dashboardData.all_findings || []).length)} of {(dashboardData.all_findings || []).length} findings
                </div>
              )}
            </CardHeader>
            <CardContent>
              {(dashboardData.all_findings || []).length > 0 ? (
                <div className="space-y-4">
                  {/* Description */}
                  <div className="p-3 bg-muted/30 rounded-lg border">
                    <p className="text-sm text-muted-foreground">
                      Complete list of all security findings across all clusters in your tenant. 
                      Use the filters above to narrow down by category or severity level.
                    </p>
                  </div>
                  
                  {/* Findings List */}
                  <div className="space-y-3">
                    {(dashboardData.all_findings || [])
                      .slice((currentPage - 1) * findingsPerPage, currentPage * findingsPerPage)
                      .map(finding => (
                      <div
                        key={finding.id}
                        className="p-4 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                        onClick={() => navigateToFinding(finding)}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              {getSeverityIcon(finding.severity)}
                              <Badge className={cn("text-xs", (finding.severity === 'high' || finding.severity === 'critical') ? "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800" : getSeverityColor(normalizeSeverity(finding.severity)))}>
                                {(finding.severity === 'high' || finding.severity === 'critical') ? 'HIGH RISK' : normalizeSeverity(finding.severity).toUpperCase()}
                              </Badge>
                              {(() => {
                                const fixStatus = getFixStatusInfo(finding);
                                return fixStatus ? (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Badge className={cn("text-xs", fixStatus.badgeClass)}>
                                        {fixStatus.badge}
                                      </Badge>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>{fixStatus.tooltip}</p>
                                      {finding.validated_at && (
                                        <p className="text-xs mt-1">Validated: {new Date(finding.validated_at).toLocaleDateString()}</p>
                                      )}
                                    </TooltipContent>
                                  </Tooltip>
                                ) : null;
                              })()}
                              <Badge variant="outline" className="text-xs">
                                {finding.category}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {finding.affected_resources_count} resources affected
                              </span>
                              {finding.has_remediation_steps && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <CheckCircle className="h-4 w-4 text-green-600" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Automated remediation available</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                            </div>
                            <h6 className="font-medium text-foreground mb-1">{finding.title}</h6>
                            <p className="text-sm text-muted-foreground line-clamp-2">{finding.description}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                              <span>Cluster: {getClusterName(finding.cluster_id)}</span>
                              <span>Found: {new Date(parseTimestamp(finding.created_at)).toLocaleDateString()}</span>
                            </div>
                          </div>
                          <ExternalLink className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {(dashboardData.all_findings || []).length > findingsPerPage && (
                    <div className="flex items-center justify-between mt-6 pt-4 border-t">
                      <div className="text-sm text-muted-foreground">
                        Page {currentPage} of {Math.ceil((dashboardData.all_findings || []).length / findingsPerPage)}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(prev => Math.min(Math.ceil((dashboardData.all_findings || []).length / findingsPerPage), prev + 1))}
                          disabled={currentPage >= Math.ceil((dashboardData.all_findings || []).length / findingsPerPage)}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                // Empty state when no findings exist
                <div className="text-center py-12">
                  <FileSearch className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                  <h3 className="text-lg font-medium mb-2 text-foreground">No Security Findings Yet</h3>
                  <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                    No security findings have been discovered yet. Use the "Run Scan" button above
                    to start a security assessment on your clusters.
                  </p>
                  <Button
                    onClick={() => setShowScanDialog(true)}
                    className="inline-flex items-center gap-2"
                  >
                    <Play className="h-4 w-4" />
                    Run Scan
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
          </TabsContent>

          {/* Risk Trends View */}
          <TabsContent value="trends" className="space-y-6">
            <RiskTrendsTab clusterId={selectedCluster === 'all' ? undefined : selectedCluster} />
          </TabsContent>

          {/* Attack Paths View */}
          <TabsContent value="attack-paths" className="space-y-6">
            {isLoadingAttackChains ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Loading attack paths...</p>
                </div>
              </div>
            ) : attackChainsError ? (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{attackChainsError}</AlertDescription>
              </Alert>
            ) : attackChains.length === 0 ? (
              <Card>
                <CardContent className="py-12">
                  <div className="text-center">
                    <Network className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No Attack Paths Found</h3>
                    <p className="text-muted-foreground">
                      {selectedCluster
                        ? 'No attack paths have been generated for this cluster yet.'
                        : 'No attack paths have been generated yet. Run security scans to generate attack path analysis.'}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <AttackChainVisualization
                attackChains={attackChains}
                onStepClick={(chainId, stepNumber, findingId) => {
                  if (findingId) {
                    // Navigate to the finding detail page using simpler route
                    navigate(`/findings/${findingId}`);
                  }
                }}
              />
            )}
          </TabsContent>
        </Tabs>
          </TabsContent>

          {/* Cloud Security Tab (AWS) */}
          <TabsContent value="cloud" className="space-y-6">
            {/* Cloud Controls Row - Account filter, tabs, and Run Scan on same row */}
            <Tabs defaultValue="cloud-dashboard" className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex flex-wrap items-center gap-3">
                  {/* Cloud Account Filter - supports both AWS and OCI */}
                  {(awsConnections.length > 0 || ociConnections.length > 0) && (
                    <Select
                      value={cloudAccountFilter}
                      onValueChange={setCloudAccountFilter}
                    >
                      <SelectTrigger className="w-full sm:w-[220px]">
                        <SelectValue placeholder="All Cloud Accounts" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Cloud Accounts</SelectItem>
                        {awsConnections.length > 0 && (
                          <>
                            <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">AWS Accounts</div>
                            {awsConnections.map((conn) => (
                              <SelectItem key={conn.id} value={`aws:${conn.id}`}>
                                {conn.account_id} ({conn.region})
                              </SelectItem>
                            ))}
                          </>
                        )}
                        {ociConnections.length > 0 && (
                          <>
                            <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">OCI Tenancies</div>
                            {ociConnections.map((conn) => (
                              <SelectItem key={conn.id} value={`oci:${conn.id}`}>
                                {conn.connection_name || conn.tenancy_ocid.substring(0, 20) + '...'}
                              </SelectItem>
                            ))}
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  )}

                  {/* Inner Tabs - Dashboard, Risk Trends, Attack Paths */}
                  {(awsConnections.length > 0 || ociConnections.length > 0) && (
                    <TabsList className="grid w-fit grid-cols-3">
                      <TabsTrigger value="cloud-dashboard" className="flex items-center gap-2">
                        <Shield className="h-4 w-4" />
                        Dashboard
                      </TabsTrigger>
                      <TabsTrigger value="cloud-trends" className="flex items-center gap-2">
                        <TrendingUp className="h-4 w-4" />
                        Risk Trends
                      </TabsTrigger>
                      <TabsTrigger value="cloud-attack-paths" className="flex items-center gap-2">
                        <Network className="h-4 w-4" />
                        Attack Paths
                      </TabsTrigger>
                    </TabsList>
                  )}

                  {/* Ongoing Cloud Assessments Indicator */}
                  {(ongoingCloudAssessments.length > 0 || ongoingOciAssessments.length > 0) && (
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md cursor-default">
                            <CircularProgress
                              value={(() => {
                                const all = [...ongoingCloudAssessments, ...ongoingOciAssessments];
                                if (all.length === 0) return 0;
                                return Math.round(all.reduce((sum, a) => sum + (a.progress ?? 0), 0) / all.length);
                              })()}
                              size={16}
                              strokeWidth={2}
                              className="text-blue-600 dark:text-blue-400"
                            />
                            <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                              {ongoingCloudAssessments.length + ongoingOciAssessments.length} assessment{(ongoingCloudAssessments.length + ongoingOciAssessments.length) > 1 ? 's' : ''} running
                            </span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" align="start" className="w-80">
                          <div className="space-y-2">
                            <h4 className="font-medium text-sm">Cloud Assessments in Progress</h4>
                            <div className="max-h-60 overflow-y-auto space-y-1">
                              {ongoingCloudAssessments.map((assessment, index) => (
                                <div
                                  key={`aws-${assessment.id}-${index}`}
                                  className="flex items-center gap-2 px-2 py-1.5 bg-muted/50 rounded-md text-sm"
                                >
                                  <CircularProgress
                                    value={assessment.progress ?? 0}
                                    size={12}
                                    strokeWidth={2}
                                    className="text-blue-600 dark:text-blue-400 flex-shrink-0"
                                  />
                                  <span className="text-xs truncate">AWS Account: {assessment.aws_account_id}</span>
                                  <span className="text-xs text-muted-foreground ml-auto">{assessment.progress ?? 0}%</span>
                                </div>
                              ))}
                              {ongoingOciAssessments.map((assessment, index) => (
                                <div
                                  key={`oci-${assessment.id}-${index}`}
                                  className="flex items-center gap-2 px-2 py-1.5 bg-muted/50 rounded-md text-sm"
                                >
                                  <CircularProgress
                                    value={assessment.progress ?? 0}
                                    size={12}
                                    strokeWidth={2}
                                    className="text-orange-600 dark:text-orange-400 flex-shrink-0"
                                  />
                                  <span className="text-xs truncate">OCI Tenancy: {assessment.oci_tenancy_ocid?.slice(-12) || 'Unknown'}</span>
                                  <span className="text-xs text-muted-foreground ml-auto">{assessment.progress ?? 0}%</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}

                  {isLoadingCloudDashboard && (
                    <RefreshCw className="h-4 w-4 text-muted-foreground animate-spin" />
                  )}
                </div>

                {/* Cloud Scan Trigger Button */}
                <Dialog open={showCloudScanDialog} onOpenChange={setShowCloudScanDialog}>
                <DialogTrigger asChild>
                  <Button className="flex items-center gap-2" disabled={awsConnections.length === 0 && ociConnections.length === 0}>
                    <Play className="h-4 w-4" />
                    Run Scan
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Run Cloud Security Assessment</DialogTitle>
                    <DialogDescription>
                      Analyze your cloud account for security misconfigurations, excessive permissions, and compliance issues.
                    </DialogDescription>
                  </DialogHeader>

                  <div className="space-y-4 py-4">
                    {/* Cloud Provider Selection */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Cloud Provider</label>
                      <Select
                        value={selectedCloudProvider}
                        onValueChange={(value) => {
                          setSelectedCloudProvider(value as 'aws' | 'oci');
                          setSelectedAwsAccount('');
                          setSelectedOciConnection('');
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select cloud provider" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="aws" disabled={awsConnections.length === 0}>
                            <div className="flex items-center gap-2">
                              AWS {awsConnections.length === 0 && '(No connections)'}
                            </div>
                          </SelectItem>
                          <SelectItem value="oci" disabled={ociConnections.length === 0}>
                            <div className="flex items-center gap-2">
                              Oracle Cloud (OCI) {ociConnections.length === 0 && '(No connections)'}
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* AWS Account Selection */}
                    {selectedCloudProvider === 'aws' && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">AWS Account</label>
                      <Select
                        value={selectedAwsAccount}
                        onValueChange={setSelectedAwsAccount}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select an AWS account" />
                        </SelectTrigger>
                        <SelectContent>
                          {awsConnections.map((conn) => (
                            <SelectItem key={conn.id} value={conn.id}>
                              <div className="flex items-center gap-2">
                                <div className={cn(
                                  "w-2 h-2 rounded-full",
                                  conn.connection_status === 'connected' ? "bg-green-500" : "bg-gray-400"
                                )} />
                                {conn.account_id}
                                <span className="text-xs text-muted-foreground">({conn.region})</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    )}

                    {/* OCI Tenancy Selection */}
                    {selectedCloudProvider === 'oci' && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">OCI Tenancy</label>
                      <Select
                        value={selectedOciConnection}
                        onValueChange={setSelectedOciConnection}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select an OCI tenancy" />
                        </SelectTrigger>
                        <SelectContent>
                          {ociConnections.map((conn) => (
                            <SelectItem key={conn.id} value={conn.id}>
                              <div className="flex items-center gap-2">
                                <div className={cn(
                                  "w-2 h-2 rounded-full",
                                  conn.connection_status === 'connected' ? "bg-green-500" : "bg-gray-400"
                                )} />
                                {conn.connection_name || conn.tenancy_ocid.substring(0, 30) + '...'}
                                <span className="text-xs text-muted-foreground">({conn.region})</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    )}

                    <div className="p-3 bg-muted/50 rounded-lg text-sm text-muted-foreground">
                      <p className="font-medium text-foreground mb-1">Assessment includes:</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>IAM roles and policies analysis</li>
                        <li>Excessive permissions detection</li>
                        <li>Cross-account trust evaluation</li>
                        <li>Resource exposure assessment</li>
                      </ul>
                    </div>

                    {cloudScanError && (
                      <Alert variant="destructive">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription className="whitespace-pre-line">
                          {cloudScanError}
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>

                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowCloudScanDialog(false);
                        setSelectedAwsAccount('');
                        setCloudScanError('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleStartCloudScan}
                      disabled={(selectedCloudProvider === 'aws' ? !selectedAwsAccount : !selectedOciConnection) || isStartingCloudScan}
                    >
                      {isStartingCloudScan ? 'Starting...' : 'Start Assessment'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            {/* Cloud Assessment Content */}
            {awsConnections.length === 0 && ociConnections.length === 0 ? (
              <Card>
                <CardContent className="py-12">
                  <div className="text-center">
                    <Cloud className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                    <h3 className="text-lg font-semibold mb-2">No Cloud Connections</h3>
                    <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                      Connect your AWS or OCI account to start running cloud security assessments.
                      Go to Cloud Integrations to set up a connection.
                    </p>
                    <Button
                      onClick={() => window.location.href = '/cloud-integrations'}
                    >
                      Set Up Cloud Connection
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <>
                  {/* Cloud Dashboard View */}
                  <TabsContent value="cloud-dashboard" className="space-y-6">
                    {/* Overview Cards - Mirroring K8s Dashboard */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <Card>
                        <CardContent className="p-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-muted-foreground">Total Findings</p>
                              <p className={cn("text-2xl font-bold", (cloudDashboardData?.summary?.total_findings || 0) === 0 ? "text-muted-foreground" : "")}>
                                {cloudDashboardData?.summary?.total_findings || 0}
                              </p>
                            </div>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <AlertTriangle className={cn("h-8 w-8", (cloudDashboardData?.summary?.total_findings || 0) === 0 ? "text-muted-foreground" : "text-orange-500")} />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{(cloudDashboardData?.summary?.total_findings || 0) === 0 ? "No security findings yet. Run cloud assessments to discover vulnerabilities." : "Total security findings across all cloud accounts"}</p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardContent className="p-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-muted-foreground">Cloud Accounts Assessed</p>
                              <p className={cn("text-2xl font-bold", (cloudDashboardData?.account_summaries?.length || 0) === 0 ? "text-muted-foreground" : "")}>
                                {cloudDashboardData?.account_summaries?.length || 0}
                              </p>
                            </div>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Server className={cn("h-8 w-8", (cloudDashboardData?.account_summaries?.length || 0) === 0 ? "text-muted-foreground" : "text-blue-500")} />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{(cloudDashboardData?.account_summaries?.length || 0) === 0 ? "No cloud accounts assessed yet. Connect accounts and run assessments." : "Number of cloud accounts (AWS/OCI) that have been assessed"}</p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardContent className="p-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-muted-foreground">Overall Risk Score</p>
                              {(() => {
                                const summary = cloudDashboardData?.summary;
                                const high = summary?.high_risk_findings || 0;
                                const medium = summary?.medium_risk_findings || 0;
                                const low = summary?.low_risk_findings || 0;
                                const total = high + medium + low;
                                const score = total > 0 ? ((high * 3 + medium * 2 + low * 1) / total) * (10/3) : 0;
                                const scoreColor = score === 0 ? "text-muted-foreground" : score >= 7 ? "text-red-600" : score >= 4 ? "text-orange-600" : "text-green-600";
                                return (
                                  <p className={cn("text-2xl font-bold", scoreColor)}>
                                    {score.toFixed(1)}/10
                                  </p>
                                );
                              })()}
                            </div>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <TrendingUp className={cn("h-8 w-8", (cloudDashboardData?.summary?.total_findings || 0) === 0 ? "text-muted-foreground" : "text-primary")} />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{(cloudDashboardData?.summary?.total_findings || 0) === 0 ? "No risk score yet. Complete assessments to see your security posture." : "Weighted average risk score: High=3, Medium=2, Low=1"}</p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardContent className="p-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-muted-foreground">Dismissed Findings</p>
                              <p className={cn("text-2xl font-bold", (cloudDashboardData?.summary?.dismissed_findings || 0) === 0 ? "text-muted-foreground" : "text-green-600")}>
                                {cloudDashboardData?.summary?.dismissed_findings || 0}
                              </p>
                            </div>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <CheckCircle className={cn("h-8 w-8", (cloudDashboardData?.summary?.dismissed_findings || 0) === 0 ? "text-muted-foreground" : "text-green-600")} />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{(cloudDashboardData?.summary?.dismissed_findings || 0) === 0 ? "No findings have been dismissed yet." : "Number of security findings that have been dismissed"}</p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Filters - Mirroring K8s Dashboard */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Filter className="h-5 w-5" />
                          Filters
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          {/* Category Filter */}
                          <div>
                            <h4 className="font-medium text-foreground mb-3">Filter by Category</h4>
                            <div className="flex flex-wrap gap-2">
                              <Button
                                variant={cloudSelectedCategories.length === 0 ? "default" : "outline"}
                                size="sm"
                                onClick={() => setCloudSelectedCategories([])}
                              >
                                All Categories
                              </Button>
                              {cloudCategories.map(category => (
                                <Button
                                  key={category}
                                  variant={cloudSelectedCategories.includes(category) ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => {
                                    if (cloudSelectedCategories.includes(category)) {
                                      setCloudSelectedCategories(prev => prev.filter(c => c !== category));
                                    } else {
                                      setCloudSelectedCategories(prev => [...prev, category]);
                                    }
                                  }}
                                  className="text-xs"
                                >
                                  {category}
                                </Button>
                              ))}
                            </div>
                          </div>

                          {/* Severity Filter */}
                          <div>
                            <h4 className="font-medium text-foreground mb-3">Filter by Severity</h4>
                            <div className="flex flex-wrap gap-2">
                              <Button
                                variant={cloudSelectedSeverities.length === 0 ? "default" : "outline"}
                                size="sm"
                                onClick={() => setCloudSelectedSeverities([])}
                              >
                                All Severities
                              </Button>
                              {['High', 'Medium', 'Low'].map(severity => (
                                <Button
                                  key={severity}
                                  variant={cloudSelectedSeverities.includes(severity.toLowerCase()) ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => {
                                    const sev = severity.toLowerCase();
                                    if (cloudSelectedSeverities.includes(sev)) {
                                      setCloudSelectedSeverities(prev => prev.filter(s => s !== sev));
                                    } else {
                                      setCloudSelectedSeverities(prev => [...prev, sev]);
                                    }
                                  }}
                                  className="text-xs capitalize"
                                >
                                  {severity}
                                </Button>
                              ))}
                            </div>
                          </div>

                          {/* Status Filter */}
                          <div>
                            <h4 className="font-medium text-foreground mb-3">Filter by Status</h4>
                            <div className="flex flex-wrap gap-2">
                              <Button
                                variant={cloudSelectedStatus.length === 0 ? "default" : "outline"}
                                size="sm"
                                onClick={() => setCloudSelectedStatus([])}
                              >
                                All Status
                              </Button>
                              <Button
                                variant={cloudSelectedStatus.includes('open') ? "default" : "outline"}
                                size="sm"
                                onClick={() => {
                                  if (cloudSelectedStatus.includes('open')) {
                                    setCloudSelectedStatus(prev => prev.filter(s => s !== 'open'));
                                  } else {
                                    setCloudSelectedStatus(prev => [...prev.filter(s => s !== 'dismissed'), 'open']);
                                  }
                                }}
                                className="text-xs"
                              >
                                <AlertTriangle className={cn("h-3 w-3 mr-1", cloudSelectedStatus.includes('open') ? "text-white" : "text-orange-600")} />
                                Open
                              </Button>
                              <Button
                                variant={cloudSelectedStatus.includes('dismissed') ? "default" : "outline"}
                                size="sm"
                                onClick={() => {
                                  if (cloudSelectedStatus.includes('dismissed')) {
                                    setCloudSelectedStatus(prev => prev.filter(s => s !== 'dismissed'));
                                  } else {
                                    setCloudSelectedStatus(prev => [...prev.filter(s => s !== 'open'), 'dismissed']);
                                  }
                                }}
                                className="text-xs"
                              >
                                <CheckCircle className={cn("h-3 w-3 mr-1", cloudSelectedStatus.includes('dismissed') ? "text-white" : "text-green-600")} />
                                Dismissed
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* By Category / All Findings Tabs - Mirroring K8s */}
                    <Tabs defaultValue="cloud-categories" className="space-y-4">
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="cloud-categories">By Category</TabsTrigger>
                        <TabsTrigger value="cloud-all-findings">All Findings</TabsTrigger>
                      </TabsList>

                      {/* By Category Tab */}
                      <TabsContent value="cloud-categories" className="space-y-4">
                        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                          {cloudCategories.map(categoryName => {
                            const CategoryIcon = cloudCategoryIcons[categoryName] || Shield;
                            const categoryFindings = cloudUniqueFindings.filter(f => f.category === categoryName && f.status === 'open');
                            const highCount = categoryFindings.filter(f => f.severity === 'high' || f.severity === 'critical').length;
                            const mediumCount = categoryFindings.filter(f => f.severity === 'medium').length;
                            const lowCount = categoryFindings.filter(f => f.severity === 'low').length;
                            const totalCount = categoryFindings.length;
                            const avgScore = totalCount > 0 ? ((highCount * 3 + mediumCount * 2 + lowCount * 1) / totalCount) * (10/3) : 0;
                            const scoreColor = avgScore === 0 ? "text-muted-foreground" : avgScore >= 7 ? "text-red-600" : avgScore >= 4 ? "text-orange-600" : "text-green-600";
                            const isExpanded = cloudExpandedCategories.has(categoryName);

                            return (
                              <Card key={categoryName} className={cn("border-l-4", totalCount > 0 ? "border-l-primary" : "border-l-muted")}>
                                <CardHeader>
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <CategoryIcon className={cn("h-6 w-6", totalCount > 0 ? "text-primary" : "text-muted-foreground")} />
                                      <div>
                                        <CardTitle className={cn("text-lg", totalCount === 0 && "text-muted-foreground")}>{categoryName}</CardTitle>
                                        <div className="flex items-center gap-2 mt-1">
                                          <Badge variant="secondary" className="text-xs">
                                            {totalCount} findings
                                          </Badge>
                                          <span className={cn("text-sm font-medium", scoreColor)}>
                                            {avgScore.toFixed(1)}/10
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => toggleCloudCategoryExpansion(categoryName)}
                                    >
                                      {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                    </Button>
                                  </div>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                  <div className="flex items-center gap-4 text-sm">
                                    <div className="flex items-center gap-1">
                                      <div className={cn("w-2 h-2 rounded-full", totalCount > 0 ? "bg-red-500" : "bg-red-500/30")}></div>
                                      <span className="text-muted-foreground">{highCount} High</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <div className={cn("w-2 h-2 rounded-full", totalCount > 0 ? "bg-orange-500" : "bg-orange-500/30")}></div>
                                      <span className="text-muted-foreground">{mediumCount} Medium</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <div className={cn("w-2 h-2 rounded-full", totalCount > 0 ? "bg-yellow-500" : "bg-yellow-500/30")}></div>
                                      <span className="text-muted-foreground">{lowCount} Low</span>
                                    </div>
                                  </div>

                                  {/* Expanded Findings */}
                                  {isExpanded && (
                                    categoryFindings.length > 0 ? (
                                      <div className="space-y-2">
                                        <Separator />
                                        <h5 className="font-medium text-foreground">Top Findings</h5>
                                        <div className="space-y-2 max-h-80 overflow-y-auto">
                                          {categoryFindings.slice(0, 5).map(finding => (
                                            <div
                                              key={finding.id}
                                              className="p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                                              onClick={() => navigateToCloudFinding(finding)}
                                            >
                                              <div className="flex items-start justify-between gap-2">
                                                <div className="flex-1 min-w-0">
                                                  <div className="flex items-center gap-2 mb-1">
                                                    <div className={cn(
                                                      "w-2 h-2 rounded-full",
                                                      finding.severity === 'critical' || finding.severity === 'high' ? "bg-red-500" :
                                                      finding.severity === 'medium' ? "bg-orange-500" : "bg-yellow-500"
                                                    )} />
                                                    <Badge variant={
                                                      finding.severity === 'critical' || finding.severity === 'high' ? 'destructive' :
                                                      finding.severity === 'medium' ? 'default' : 'secondary'
                                                    } className="text-xs capitalize">
                                                      {finding.severity}
                                                    </Badge>
                                                    <span className="text-xs text-muted-foreground">
                                                      {finding.affected_resources_count} resources
                                                    </span>
                                                  </div>
                                                  <h6 className="font-medium text-sm text-foreground truncate">{finding.title}</h6>
                                                  <p className="text-xs text-muted-foreground line-clamp-2">{finding.description}</p>
                                                </div>
                                                <ExternalLink className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                              </div>
                                            </div>
                                          ))}
                                          {categoryFindings.length > 5 && (
                                            <div className="text-center py-2 text-xs text-muted-foreground border-t">
                                              Showing top 5 of {categoryFindings.length} findings
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="space-y-2">
                                        <Separator />
                                        <div className="text-center py-6 text-muted-foreground">
                                          <Shield className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                          <p className="text-sm font-medium">No Security Findings Yet</p>
                                          <p className="text-xs mt-1">No security findings have been discovered yet. Use the "Run Scan" button above to start a security assessment on your cloud account.</p>
                                        </div>
                                      </div>
                                    )
                                  )}
                                </CardContent>
                              </Card>
                            );
                          })}
                        </div>
                      </TabsContent>

                      {/* All Findings Tab */}
                      <TabsContent value="cloud-all-findings" className="space-y-4">
                        <Card>
                          <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle className="flex items-center gap-2">
                              <FileSearch className="h-5 w-5" />
                              All Findings
                              <Badge variant="outline" className="ml-2">
                                {(() => {
                                  let filtered = cloudUniqueFindings;
                                  if (cloudSelectedCategories.length > 0) {
                                    filtered = filtered.filter(f => cloudSelectedCategories.includes(f.category));
                                  }
                                  if (cloudSelectedSeverities.length > 0) {
                                    filtered = filtered.filter(f => cloudSelectedSeverities.includes(f.severity));
                                  }
                                  if (cloudSelectedStatus.length > 0) {
                                    filtered = filtered.filter(f => cloudSelectedStatus.includes(f.status));
                                  }
                                  return filtered.length;
                                })()} total findings
                              </Badge>
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            {cloudDashboardError && (
                              <Alert variant="destructive" className="mb-4">
                                <AlertTriangle className="h-4 w-4" />
                                <AlertDescription>{cloudDashboardError}</AlertDescription>
                              </Alert>
                            )}
                            {cloudUniqueFindings.length === 0 ? (
                              <div className="text-center py-8">
                                <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                                <h3 className="text-lg font-semibold mb-2">No Security Findings Yet</h3>
                                <p className="text-muted-foreground mb-4">
                                  No security findings have been discovered yet. Use the "Run Scan" button above to start a security assessment on your cloud account.
                                </p>
                              </div>
                            ) : (
                              <div className="space-y-3">
                                {(() => {
                                  let filtered = cloudUniqueFindings;
                                  if (cloudSelectedCategories.length > 0) {
                                    filtered = filtered.filter(f => cloudSelectedCategories.includes(f.category));
                                  }
                                  if (cloudSelectedSeverities.length > 0) {
                                    filtered = filtered.filter(f => cloudSelectedSeverities.includes(f.severity));
                                  }
                                  if (cloudSelectedStatus.length > 0) {
                                    filtered = filtered.filter(f => cloudSelectedStatus.includes(f.status));
                                  }
                                  return filtered.slice(0, 20).map((finding) => (
                                    <div
                                      key={finding.id}
                                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                                      onClick={() => navigateToCloudFinding(finding)}
                                    >
                                      <div className="flex items-center gap-4 flex-1 min-w-0">
                                        <div className={cn(
                                          "w-3 h-3 rounded-full flex-shrink-0",
                                          finding.severity === 'critical' || finding.severity === 'high' ? "bg-red-500" :
                                          finding.severity === 'medium' ? "bg-orange-500" :
                                          "bg-yellow-500"
                                        )} />
                                        <div className="min-w-0 flex-1">
                                          <div className="font-medium truncate">{finding.title}</div>
                                          <div className="text-sm text-muted-foreground truncate">
                                            {finding.category} • {finding.affected_resources_count} resource{finding.affected_resources_count !== 1 ? 's' : ''}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-3 flex-shrink-0">
                                        <Badge variant={
                                          finding.severity === 'critical' || finding.severity === 'high' ? 'destructive' :
                                          finding.severity === 'medium' ? 'default' :
                                          'secondary'
                                        } className="capitalize">
                                          {finding.severity}
                                        </Badge>
                                        <Badge variant={
                                          finding.status === 'open' ? 'outline' :
                                          finding.status === 'dismissed' ? 'secondary' :
                                          'default'
                                        } className="capitalize">
                                          {finding.status}
                                        </Badge>
                                        {finding.occurrence_count > 1 && (
                                          <Badge variant="secondary" className="text-xs">
                                            {finding.occurrence_count}x
                                          </Badge>
                                        )}
                                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                      </div>
                                    </div>
                                  ));
                                })()}
                                {(() => {
                                  let filtered = cloudUniqueFindings;
                                  if (cloudSelectedCategories.length > 0) {
                                    filtered = filtered.filter(f => cloudSelectedCategories.includes(f.category));
                                  }
                                  if (cloudSelectedSeverities.length > 0) {
                                    filtered = filtered.filter(f => cloudSelectedSeverities.includes(f.severity));
                                  }
                                  if (cloudSelectedStatus.length > 0) {
                                    filtered = filtered.filter(f => cloudSelectedStatus.includes(f.status));
                                  }
                                  if (filtered.length > 20) {
                                    return (
                                      <div className="text-center py-2">
                                        <span className="text-sm text-muted-foreground">
                                          Showing 20 of {filtered.length} findings
                                        </span>
                                      </div>
                                    );
                                  }
                                  return null;
                                })()}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </TabsContent>
                    </Tabs>
                  </TabsContent>

                  {/* Cloud Risk Trends View */}
                  <TabsContent value="cloud-trends" className="space-y-6">
                    <CloudRiskTrendsTab
                      awsConnectionId={
                        cloudAccountFilter === 'all' ? undefined :
                        cloudAccountFilter.startsWith('aws:') ? cloudAccountFilter.split(':')[1] : undefined
                      }
                      ociConnectionId={
                        cloudAccountFilter === 'all' ? undefined :
                        cloudAccountFilter.startsWith('oci:') ? cloudAccountFilter.split(':')[1] : undefined
                      }
                    />
                  </TabsContent>

                  {/* Cloud Attack Paths View */}
                  <TabsContent value="cloud-attack-paths" className="space-y-6">
                    {isLoadingCloudAttackChains ? (
                      <div className="flex items-center justify-center py-12">
                        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
                      </div>
                    ) : cloudAttackChainsError ? (
                      <Alert variant="destructive">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription className="flex items-center justify-between">
                          <span>{cloudAttackChainsError}</span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setCloudAttackChainsError('');
                              loadCloudAttackChains();
                            }}
                          >
                            Retry
                          </Button>
                        </AlertDescription>
                      </Alert>
                    ) : (
                      <CloudAttackChainVisualization
                        attackChains={cloudAttackChains}
                        showGenerateButton={cloudAccountFilter !== 'all' && (cloudAccountFilter.startsWith('aws:') || cloudAccountFilter.startsWith('oci:'))}
                        onGenerate={handleGenerateCloudAttackChains}
                        isGenerating={isGeneratingCloudAttackChains}
                        onStepClick={(chainId, stepNumber, findingId) => {
                          if (findingId) {
                            navigate(`/cloud-findings/${findingId}`);
                          }
                        }}
                      />
                    )}
                  </TabsContent>
                </>
              )}
            </Tabs>
          </TabsContent>
        </Tabs>
      </div>

      {/* Cloud Assessment Details Dialog */}
      <Dialog open={showCloudAssessmentDetails} onOpenChange={setShowCloudAssessmentDetails}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cloud className="h-5 w-5" />
              Cloud Risk Assessment Details
            </DialogTitle>
            <DialogDescription>
              {selectedCloudAssessment && (
                <span>AWS Account: {selectedCloudAssessment.aws_account_id} • {new Date(selectedCloudAssessment.created_at).toLocaleString()}</span>
              )}
            </DialogDescription>
          </DialogHeader>

          {selectedCloudAssessment && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground">Status</div>
                    <Badge variant={
                      selectedCloudAssessment.status === 'completed' ? 'default' :
                      selectedCloudAssessment.status === 'failed' ? 'destructive' :
                      'secondary'
                    } className="mt-1">
                      {selectedCloudAssessment.status}
                    </Badge>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground">High Risk</div>
                    <div className="text-2xl font-bold text-red-600">{selectedCloudAssessment.high_risk_findings}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground">Medium Risk</div>
                    <div className="text-2xl font-bold text-orange-600">{selectedCloudAssessment.medium_risk_findings}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground">Low Risk</div>
                    <div className="text-2xl font-bold text-yellow-600">{selectedCloudAssessment.low_risk_findings}</div>
                  </CardContent>
                </Card>
              </div>

              {/* Structured Findings */}
              {selectedCloudAssessment.structured_findings && selectedCloudAssessment.structured_findings.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3">Security Findings</h3>
                  <div className="space-y-3">
                    {selectedCloudAssessment.structured_findings.map((finding, index) => (
                      <Card key={finding.id || index} className={cn(
                        "border-l-4",
                        finding.severity === 'high' ? "border-l-red-500" :
                        finding.severity === 'medium' ? "border-l-orange-500" :
                        "border-l-yellow-500"
                      )}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge variant={
                                  finding.severity === 'high' ? 'destructive' :
                                  finding.severity === 'medium' ? 'default' :
                                  'secondary'
                                }>
                                  {finding.severity.toUpperCase()}
                                </Badge>
                                <Badge variant="outline">{finding.category}</Badge>
                              </div>
                              <h4 className="font-semibold mb-1">{finding.title}</h4>
                              <p className="text-sm text-muted-foreground mb-3">{finding.description}</p>

                              {finding.recommendation && (
                                <div className="bg-muted/50 p-3 rounded-md mb-3">
                                  <div className="text-xs font-semibold text-muted-foreground mb-1">RECOMMENDATION</div>
                                  <p className="text-sm">{finding.recommendation}</p>
                                </div>
                              )}

                              {finding.affected_resources && finding.affected_resources.length > 0 && (
                                <div>
                                  <div className="text-xs font-semibold text-muted-foreground mb-1">AFFECTED RESOURCES</div>
                                  <div className="flex flex-wrap gap-2">
                                    {finding.affected_resources.map((resource, rIndex) => (
                                      <Badge key={rIndex} variant="outline" className="text-xs">
                                        {resource.resource_type}: {resource.name}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Final Report */}
              {selectedCloudAssessment.final_report && (
                <div>
                  <h3 className="text-lg font-semibold mb-3">Full Assessment Report</h3>
                  <Card>
                    <CardContent className="p-4">
                      <pre className="whitespace-pre-wrap text-sm font-mono bg-muted/30 p-4 rounded-md overflow-auto max-h-96">
                        {selectedCloudAssessment.final_report}
                      </pre>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Error Message */}
              {selectedCloudAssessment.error_message && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{selectedCloudAssessment.error_message}</AlertDescription>
                </Alert>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCloudAssessmentDetails(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cloud Finding Details Dialog */}
      <Dialog open={showCloudFindingDetails} onOpenChange={setShowCloudFindingDetails}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Cloud Security Finding
            </DialogTitle>
            <DialogDescription>
              {selectedCloudFinding && (
                <span>{selectedCloudFinding.category} • First seen: {new Date(selectedCloudFinding.first_seen_at).toLocaleDateString()}</span>
              )}
            </DialogDescription>
          </DialogHeader>

          {selectedCloudFinding && (
            <div className="space-y-6">
              {/* Finding Header */}
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold">{selectedCloudFinding.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{selectedCloudFinding.description}</p>
                </div>
                <div className="flex flex-col gap-2 items-end">
                  <Badge variant={
                    selectedCloudFinding.severity === 'critical' || selectedCloudFinding.severity === 'high' ? 'destructive' :
                    selectedCloudFinding.severity === 'medium' ? 'default' :
                    'secondary'
                  } className="capitalize">
                    {selectedCloudFinding.severity}
                  </Badge>
                  <Badge variant={
                    selectedCloudFinding.status === 'open' ? 'outline' :
                    selectedCloudFinding.status === 'dismissed' ? 'secondary' :
                    'default'
                  } className="capitalize">
                    {selectedCloudFinding.status}
                  </Badge>
                </div>
              </div>

              {/* Stats Row */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground">Affected Resources</div>
                    <div className="text-2xl font-bold">{selectedCloudFinding.affected_resources_count}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground">Occurrences</div>
                    <div className="text-2xl font-bold">{selectedCloudFinding.occurrence_count}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground">Last Seen</div>
                    <div className="text-sm font-medium">{new Date(selectedCloudFinding.last_seen_at).toLocaleString()}</div>
                  </CardContent>
                </Card>
              </div>

              {/* Recommendation */}
              {selectedCloudFinding.recommendation && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Recommendation</h4>
                  <div className="p-3 bg-muted rounded-lg text-sm">
                    {selectedCloudFinding.recommendation}
                  </div>
                </div>
              )}

              {/* Affected Resources */}
              {selectedCloudFinding.affected_resources && selectedCloudFinding.affected_resources.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Affected Resources</h4>
                  <div className="border rounded-lg divide-y max-h-40 overflow-y-auto">
                    {selectedCloudFinding.affected_resources.slice(0, 10).map((resource, index) => (
                      <div key={index} className="p-2 text-sm flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 min-w-0">
                        <span className="text-muted-foreground flex-shrink-0">{resource.resource_type}:</span>
                        <span className="font-mono break-all">{resource.name}</span>
                        {resource.arn && (
                          <span className="text-xs text-muted-foreground truncate sm:ml-auto block" title={resource.arn}>
                            {resource.arn.substring(0, 50)}...
                          </span>
                        )}
                      </div>
                    ))}
                    {selectedCloudFinding.affected_resources.length > 10 && (
                      <div className="p-2 text-sm text-muted-foreground text-center">
                        ... and {selectedCloudFinding.affected_resources.length - 10} more resources
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Dismissed Info */}
              {selectedCloudFinding.status === 'dismissed' && selectedCloudFinding.dismissed_at && (
                <Alert>
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>
                    Dismissed on {new Date(selectedCloudFinding.dismissed_at).toLocaleString()}
                    {selectedCloudFinding.dismissed_reason && (
                      <span> - Reason: {selectedCloudFinding.dismissed_reason}</span>
                    )}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2">
            {selectedCloudFinding && selectedCloudFinding.status === 'open' && (
              <Button
                variant="outline"
                onClick={() => handleDismissCloudFinding(selectedCloudFinding.id, 'Manually dismissed from dashboard')}
                disabled={isDismissingFinding}
                className="sm:mr-auto"
              >
                {isDismissingFinding ? 'Dismissing...' : 'Dismiss Finding'}
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowCloudFindingDetails(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
};

export default VulnerabilityDashboard; 